import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
    Box, Card, Typography, Button, IconButton, TextField, InputAdornment,
    ToggleButton, ToggleButtonGroup, Chip, Grid, CircularProgress, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Send as SendIcon,
    PersonSearch as PersonSearchIcon,
    DeleteOutline as DeleteOutlineIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import useDictation from '../../../hooks/useDictation';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import { getPtoOverview, createPtoRequest, newClientId } from '../../../services/ptoService';
import { localDateString, parseLocalDate, formatDateUS } from '../../../utils/localDate';

// Lift SweetAlert (toasts + dialogs) above the app's update-notification banner,
// which is position:fixed with z-index:10000 in Header.jsx and would otherwise
// cover top-anchored toasts.
if (typeof document !== 'undefined' && !document.getElementById('pto-swal-zfix')) {
    const s = document.createElement('style');
    s.id = 'pto-swal-zfix';
    s.textContent = '.swal2-container{z-index:20000 !important;}';
    document.head.appendChild(s);
}

const EPS = 0.001;
const PORTIONS = [0.25, 0.5, 0.75, 1];
const PORTION_LABEL = { 0.25: '¼', 0.5: '½', 0.75: '¾', 1: '1' };
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

function buildCalendarGrid(year, month) {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1; // Mon = 0

    const days = [];
    for (let i = 0; i < startDow; i++) {
        const d = new Date(year, month, -startDow + i + 1);
        days.push({ date: localDateString(d), isCurrentMonth: false });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
        const d = new Date(year, month, i);
        days.push({ date: localDateString(d), isCurrentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        const d = new Date(year, month + 1, i);
        days.push({ date: localDateString(d), isCurrentMonth: false });
    }
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
}

function num(n) {
    return Number(n || 0).toFixed(2);
}

// Append a dictated chunk to an existing field value (space-aware), like JHA.
function appendText(current, transcript) {
    const cur = current || '';
    const needsSpace = cur && !cur.endsWith(' ');
    return cur + (needsSpace ? ' ' : '') + transcript + ' ';
}

export default function PtoRequestPage() {
    const navigate = useNavigate();

    const [employee, setEmployee] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);

    const [overview, setOverview] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(false);

    const [type, setType] = useState('pto');
    const [selectedDays, setSelectedDays] = useState({}); // { 'YYYY-MM-DD': portion }
    const [dayNotes, setDayNotes] = useState({}); // { 'YYYY-MM-DD': note } — per-day override
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const now = new Date();
    const [viewYear, setViewYear] = useState(now.getFullYear());
    const [viewMonth, setViewMonth] = useState(now.getMonth());

    // Voice dictation (same approach as JHA): one active field at a time.
    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000,
    });
    const [activeField, setActiveField] = useState(null);
    const dictFieldRef = useRef(null);

    const balance = overview?.balance ?? null;
    const remaining = balance ? Number(balance.remaining) : 0;

    // Day-portion already booked per date for this employee (from live local grants).
    // Drives the calendar markers and caps each day at a full 1.0.
    const bookedByDate = useMemo(() => {
        const map = {};
        for (const r of (overview?.history?.local ?? [])) {
            if (r.status !== 'submitted' && r.status !== 'approved') continue;
            for (const d of (r.days ?? [])) {
                if (d.date) map[d.date] = (map[d.date] || 0) + Number(d.portion || 0);
            }
        }
        return map;
    }, [overview]);

    const sortedDates = useMemo(() => Object.keys(selectedDays).sort(), [selectedDays]);
    const total = useMemo(
        () => sortedDates.reduce((acc, d) => acc + Number(selectedDays[d] || 0), 0),
        [sortedDates, selectedDays]
    );
    const overBalance = type === 'pto' && total > remaining + EPS;

    const weeks = useMemo(() => buildCalendarGrid(viewYear, viewMonth), [viewYear, viewMonth]);
    const today = localDateString();
    // Нижняя граница выбора — ПОНЕДЕЛЬНИК текущей недели, а не сегодня.
    // Отпуск заводят на неделю целиком, и если открыть приложение во вторник,
    // понедельник того же отпуска оказывался недоступен: приходилось писать
    // в офис, чтобы дописали один день руками.
    const minDate = useMemo(() => {
        const d = parseLocalDate(today) || new Date();
        // getDay(): 0 — воскресенье. Рабочая неделя считается с понедельника.
        const shift = (d.getDay() + 6) % 7;
        d.setDate(d.getDate() - shift);
        return localDateString(d);
    }, [today]);

    const toast = (icon, title) =>
        Swal.fire({ toast: true, position: 'top-end', icon, title, showConfirmButton: false, timer: 2200, timerProgressBar: true });

    const loadOverview = async (employeeId) => {
        setLoadingOverview(true);
        try {
            const data = await getPtoOverview(employeeId);
            setOverview(data);
        } catch (err) {
            console.error('Failed to load PTO overview', err);
            setOverview(null);
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.error || 'Failed to load employee balance' });
        } finally {
            setLoadingOverview(false);
        }
    };

    const handleSelectEmployee = (emp) => {
        if (isListening) stopDictation();
        setActiveField(null);
        dictFieldRef.current = null;
        setEmployee(emp);
        setOverview(null);
        setSelectedDays({});
        setDayNotes({});
        setReason('');
        if (emp?.id) loadOverview(emp.id);
    };

    const navigateMonth = (dir) => {
        let m = viewMonth + dir;
        let y = viewYear;
        if (m < 0) { m = 11; y -= 1; }
        if (m > 11) { m = 0; y += 1; }
        setViewMonth(m);
        setViewYear(y);
    };

    const pruneNote = (date) => setDayNotes((prev) => {
        if (!(date in prev)) return prev;
        const next = { ...prev };
        delete next[date];
        return next;
    });

    const toggleDay = (date) => {
        // Remove if already selected.
        if (selectedDays[date] != null) {
            setSelectedDays((prev) => { const next = { ...prev }; delete next[date]; return next; });
            pruneNote(date);
            return;
        }
        // Add: cap by what's free on this day (1.0 − already booked) and, for PTO,
        // by the remaining balance. Snap to the largest portion that fits.
        const perDayFree = 1 - (bookedByDate[date] || 0);
        const cap = type === 'pto' ? Math.min(perDayFree, remaining - total) : perDayFree;
        if (cap <= EPS) {
            if (perDayFree <= EPS) toast('warning', 'This day is already a full day off.');
            else toast('warning', `No vacation balance left (remaining ${num(remaining)}).`);
            return;
        }
        const portion = PORTIONS.filter((p) => p <= cap + EPS).pop() ?? 0.25;
        setSelectedDays((prev) => ({ ...prev, [date]: portion }));
        if (portion < 1) toast('info', `Added ${PORTION_LABEL[portion]} (max that fits this day).`);
    };

    const setDayPortion = (date, portion) => {
        if (portion == null) return;
        const perDayMax = 1 - (bookedByDate[date] || 0);
        if (portion > perDayMax + EPS) {
            toast('warning', `This day already has ${num(bookedByDate[date] || 0)} booked — max ${num(perDayMax)} more.`);
            return;
        }
        if (type === 'pto') {
            const totalWithout = total - (selectedDays[date] || 0);
            if (totalWithout + portion > remaining + EPS) {
                toast('warning', `Exceeds the remaining balance (${num(remaining)}).`);
                return;
            }
        }
        setSelectedDays((prev) => ({ ...prev, [date]: portion }));
    };

    const setDayNote = (date, value) => {
        setDayNotes((prev) => ({ ...prev, [date]: value }));
    };

    // field is 'reason' for the general note, or a 'YYYY-MM-DD' for a per-day note.
    const toggleDictation = useCallback(async (field) => {
        if (!dictationSupported) return;
        if (isListening && activeField === field) {
            stopDictation();
            setActiveField(null);
            dictFieldRef.current = null;
            return;
        }
        if (isListening) stopDictation(); // switch to another field
        setActiveField(field);
        dictFieldRef.current = field;
        await startDictation({
            onResult: (transcript) => {
                const key = dictFieldRef.current;
                if (!transcript || !key) return;
                if (key === 'reason') {
                    setReason((prev) => appendText(prev, transcript));
                } else {
                    setDayNotes((prev) => ({ ...prev, [key]: appendText(prev[key], transcript) }));
                }
            },
            onError: (e) => console.error('Dictation error:', e?.error || e),
        });
    }, [dictationSupported, isListening, activeField, startDictation, stopDictation]);

    const setAllPortions = (portion) => {
        if (portion == null) return;
        const next = {};
        let sum = 0;
        for (const d of Object.keys(selectedDays)) {
            const v = Math.min(portion, 1 - (bookedByDate[d] || 0)); // respect each day's free room
            next[d] = v;
            sum += v;
        }
        if (type === 'pto' && sum > remaining + EPS) {
            toast('warning', `That exceeds the remaining balance (${num(remaining)}).`);
            return;
        }
        setSelectedDays(next);
    };

    const clearDays = () => { setSelectedDays({}); setDayNotes({}); };

    const handleSubmit = async () => {
        if (!employee) {
            Swal.fire({ icon: 'warning', title: 'Select an employee', text: 'Pick an employee first.' });
            return;
        }
        if (sortedDates.length === 0) {
            Swal.fire({ icon: 'warning', title: 'No days selected', text: 'Tap days on the calendar to grant leave.' });
            return;
        }
        const years = new Set(sortedDates.map((d) => d.slice(0, 4)));
        if (years.size > 1) {
            Swal.fire({ icon: 'error', title: 'One year at a time', text: 'All selected days must be in the same year.' });
            return;
        }
        if (type === 'pto' && overBalance) {
            Swal.fire({ icon: 'error', title: 'Over the balance', text: `Requested ${num(total)} days exceeds the remaining balance of ${num(remaining)}.` });
            return;
        }

        const confirm = await Swal.fire({
            title: `Grant ${num(total)} ${type === 'pto' ? 'vacation' : 'bereavement'} day(s)?`,
            html: `Employee: <b>${employee.FullName}</b><br>${sortedDates.length} day(s) selected`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, grant it',
        });
        if (!confirm.isConfirmed) return;

        setSubmitting(true);
        try {
            const payload = {
                employeeId: employee.id,
                type,
                reason: reason.trim() || null,
                clientId: newClientId(),
                days: sortedDates.map((d) => ({
                    date: d,
                    portion: selectedDays[d],
                    note: (dayNotes[d] || '').trim() || undefined,
                })),
            };
            const res = await createPtoRequest(payload);
            const grantedTotal = total;
            // Optimistic: reflect the new grant + balance instantly (no reload needed).
            if (res?.request) {
                setOverview((prev) => (prev ? {
                    ...prev,
                    balance: res.balance ?? prev.balance,
                    history: { ...(prev.history || {}), local: [res.request, ...((prev.history && prev.history.local) || [])] },
                } : prev));
            }
            setSelectedDays({});
            setDayNotes({});
            setReason('');
            Swal.fire({ icon: 'success', title: 'Granted!', text: `${num(grantedTotal)} ${type === 'pto' ? 'vacation' : 'bereavement'} day(s) granted to ${employee.FullName}.` });
            // Reconcile with a fresh server read (also picks up other foremen's changes).
            try { setOverview(await getPtoOverview(employee.id)); } catch (e) { /* keep optimistic state */ }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.error || 'Failed to submit request. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100%' }}>
            {/* Header (sticks just below the app bar while scrolling) */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, position: 'sticky', top: 60, zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/main')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>PTO / Bereavement Request</Typography>
                    <Chip label="In Development" color="warning" size="small" sx={{ ml: 1 }} />
                </Box>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Employee"
                            placeholder="Tap to select"
                            value={employee?.FullName || ''}
                            onClick={() => setPickerOpen(true)}
                            InputProps={{
                                readOnly: true,
                                style: { cursor: 'pointer' },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setPickerOpen(true)}>
                                            <PersonSearchIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <ToggleButtonGroup
                            exclusive
                            color="primary"
                            size="small"
                            value={type}
                            onChange={(e, v) => v && setType(v)}
                        >
                            <ToggleButton value="pto">PTO / Vacation</ToggleButton>
                            <ToggleButton value="bereavement">Bereavement</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
            </Box>

            {/* Content (the page itself scrolls in the layout) */}
            <Box sx={{ p: 2 }}>
                {!employee && (
                    <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">Select an employee to view their balance and grant leave.</Typography>
                    </Card>
                )}

                {loadingOverview && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {employee && balance && !loadingOverview && (
                    <Grid container spacing={2}>
                        {/* Balance */}
                        <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                    Vacation balance — {balance.year}
                                </Typography>
                                {!balance.hasVacationRecord && (
                                    <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                                        No vacation ledger found for this employee (no EE match in EmpVac).
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                    <Metric label="Earned" value={num(balance.earned)} />
                                    <Metric label="Used" value={num(balance.used)} />
                                    <Metric label="Pending (app)" value={num(balance.pendingLocal)} />
                                    <Metric label="Remaining" value={num(balance.remaining)} highlight />
                                    <Metric label="Bereavement used" value={num(balance.bereavementUsed)} muted />
                                </Box>
                            </Card>
                        </Grid>

                        {/* Calendar (multi-date) */}
                        <Grid item xs={12} md={7}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>Tap days to select</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <IconButton size="small" onClick={() => navigateMonth(-1)}><ChevronLeftIcon /></IconButton>
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 130, textAlign: 'center' }}>
                                            {MONTH_NAMES[viewMonth]} {viewYear}
                                        </Typography>
                                        <IconButton size="small" onClick={() => navigateMonth(1)}><ChevronRightIcon /></IconButton>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', mb: 0.5 }}>
                                    {DAY_NAMES.map((d) => (
                                        <Box key={d} sx={{ flex: 1, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', py: 0.5 }}>
                                            {d}
                                        </Box>
                                    ))}
                                </Box>

                                {weeks.map((week, wi) => (
                                    <Box key={wi} sx={{ display: 'flex' }}>
                                        {week.map((day) => {
                                            const isSelected = selectedDays[day.date] != null;
                                            const booked = bookedByDate[day.date] || 0;
                                            const isFull = booked >= 1 - EPS && !isSelected;
                                            const isToday = day.date === today;
                                            const isPast = day.date < minDate;
                                            // Прошедшие дни ТЕКУЩЕЙ недели выбирать можно,
                                            // но видно, что они уже прошли.
                                            const isEarlierThisWeek = !isPast && day.date < today;
                                            const dayNum = parseLocalDate(day.date)?.getDate();
                                            return (
                                                <Box
                                                    key={day.date}
                                                    component="button"
                                                    type="button"
                                                    disabled={isPast}
                                                    onClick={() => { if (!isPast) toggleDay(day.date); }}
                                                    sx={{
                                                        flex: 1,
                                                        aspectRatio: '1',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: isToday && !isSelected ? '1px solid' : 'none',
                                                        borderColor: 'primary.main',
                                                        borderRadius: 1,
                                                        cursor: isPast ? 'not-allowed' : 'pointer',
                                                        bgcolor: isSelected ? 'primary.main' : isFull ? 'success.light' : booked > 0 ? 'warning.light' : 'transparent',
                                                        color: isSelected ? 'white' : (isPast || !day.isCurrentMonth) ? 'text.disabled' : 'text.primary',
                                                        opacity: isPast ? 0.45 : isEarlierThisWeek ? 0.72 : 1,
                                                        fontSize: '0.8rem',
                                                        fontWeight: isToday || isSelected ? 700 : 400,
                                                        minHeight: 38,
                                                        p: 0.25,
                                                        '&:hover': { bgcolor: isPast ? 'transparent' : isSelected ? 'primary.dark' : booked > 0 ? undefined : 'action.hover' },
                                                    }}
                                                >
                                                    {dayNum}
                                                    {isSelected ? (
                                                        <Typography sx={{ fontSize: '0.6rem', lineHeight: 1, color: 'rgba(255,255,255,0.85)' }}>
                                                            {PORTION_LABEL[selectedDays[day.date]]}
                                                        </Typography>
                                                    ) : booked > 0 ? (
                                                        <Typography sx={{ fontSize: '0.58rem', lineHeight: 1, color: 'text.secondary' }}>
                                                            {num(booked)}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ))}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5, fontSize: '0.7rem', color: 'text.secondary' }}>
                                    <LegendSwatch color="primary.main" label="Selecting now" />
                                    <LegendSwatch color="warning.light" label="Partly booked" />
                                    <LegendSwatch color="success.light" label="Full day booked" />
                                </Box>
                            </Card>
                        </Grid>

                        {/* Selected days + portions */}
                        <Grid item xs={12} md={5}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Selected days ({sortedDates.length})
                                    </Typography>
                                    {sortedDates.length > 0 && (
                                        <Button size="small" onClick={clearDays}>Clear</Button>
                                    )}
                                </Box>

                                {sortedDates.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No days yet — tap days on the calendar.
                                    </Typography>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography variant="caption" color="text.secondary">Set all:</Typography>
                                            <ToggleButtonGroup exclusive size="small" onChange={(e, v) => setAllPortions(v)}>
                                                {PORTIONS.map((p) => (
                                                    <ToggleButton key={p} value={p} sx={{ px: 1 }}>{PORTION_LABEL[p]}</ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                        </Box>
                                        <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                                            {sortedDates.map((d) => {
                                                const dObj = parseLocalDate(d);
                                                const dow = dObj ? dObj.toLocaleDateString('en-US', { weekday: 'short' }) : '';
                                                const dayBooked = bookedByDate[d] || 0;
                                                const dayMax = type === 'pto'
                                                    ? Math.min(1 - dayBooked, remaining - (total - (selectedDays[d] || 0)))
                                                    : 1 - dayBooked;
                                                return (
                                                    <Box key={d} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2">{formatDateUS(d)}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {dow}{dayBooked > 0 ? ` · ${num(dayBooked)} already booked` : ''}
                                                                </Typography>
                                                            </Box>
                                                            <ToggleButtonGroup exclusive size="small" value={selectedDays[d]} onChange={(e, v) => setDayPortion(d, v)}>
                                                                {PORTIONS.map((p) => (
                                                                    <ToggleButton key={p} value={p} disabled={p > dayMax + EPS} sx={{ px: 1 }}>{PORTION_LABEL[p]}</ToggleButton>
                                                                ))}
                                                            </ToggleButtonGroup>
                                                            <IconButton size="small" onClick={() => toggleDay(d)}>
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                        <TextField
                                                            value={dayNotes[d] || ''}
                                                            onChange={(e) => setDayNote(d, e.target.value)}
                                                            placeholder="Note for this day (optional — overrides general)"
                                                            variant="standard"
                                                            fullWidth
                                                            size="small"
                                                            inputProps={{ maxLength: 255 }}
                                                            InputProps={dictationSupported ? {
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => toggleDictation(d)}
                                                                            color={isListening && activeField === d ? 'error' : 'default'}
                                                                            title={isListening && activeField === d ? 'Stop recording' : 'Start voice input'}
                                                                        >
                                                                            {isListening && activeField === d ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            } : undefined}
                                                            sx={{ mt: 0.5 }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                            <Chip label={`Total: ${num(total)} day(s)`} color={overBalance ? 'error' : 'primary'} />
                                            {type === 'pto' && (
                                                <Typography variant="body2" color={overBalance ? 'error' : 'text.secondary'}>
                                                    Remaining after: {num(remaining - total)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </>
                                )}

                                <TextField
                                    label="General reason / note (default for all days)"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    sx={{ mt: 2 }}
                                    inputProps={{ maxLength: 500 }}
                                    InputProps={dictationSupported ? {
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleDictation('reason')}
                                                    color={isListening && activeField === 'reason' ? 'error' : 'default'}
                                                    title={isListening && activeField === 'reason' ? 'Stop recording' : 'Start voice input'}
                                                >
                                                    {isListening && activeField === 'reason' ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    } : undefined}
                                    helperText={isListening && activeField === 'reason' ? 'Recording… speak clearly' : 'Days with their own note override this.'}
                                />
                            </Card>
                        </Grid>

                        {/* Submit */}
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" sx={{ py: 1 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    startIcon={<SendIcon />}
                                    onClick={handleSubmit}
                                    disabled={submitting || sortedDates.length === 0 || (type === 'pto' && overBalance)}
                                    sx={{ minWidth: 240 }}
                                >
                                    {submitting
                                        ? 'Submitting...'
                                        : sortedDates.length === 0
                                            ? 'Select days to grant'
                                            : `Grant ${num(total)} day(s)`}
                                </Button>
                            </Box>
                        </Grid>

                        {/* History */}
                        <Grid item xs={12}>
                            <HistoryPanel history={overview.history} />
                        </Grid>
                    </Grid>
                )}
            </Box>

            <SelectSingleEmployeeModal
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={handleSelectEmployee}
                selectedEmployee={employee}
                title="Select Employee for PTO / Bereavement"
            />
        </Box>
    );
}

function LegendSwatch({ color, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: color, border: '1px solid', borderColor: 'divider' }} />
            <span>{label}</span>
        </Box>
    );
}

function Metric({ label, value, highlight, muted }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography
                variant="h6"
                fontWeight={highlight ? 700 : 500}
                color={highlight ? 'primary.main' : muted ? 'text.secondary' : 'text.primary'}
            >
                {value}
            </Typography>
        </Box>
    );
}

function HistoryPanel({ history }) {
    const ledger = history?.ledger ?? [];
    const local = history?.local ?? [];

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>History — {history?.year}</Typography>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Company ledger (EmpVac)</Typography>
            {ledger.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No ledger entries.</Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Week ending</TableCell>
                                <TableCell align="right">Earned</TableCell>
                                <TableCell align="right">Used</TableCell>
                                <TableCell align="right">Bereavement</TableCell>
                                <TableCell>Notes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ledger.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell>{r.weekEnding ? formatDateUS(r.weekEnding) : '—'}</TableCell>
                                    <TableCell align="right">{r.earned != null ? num(r.earned) : ''}</TableCell>
                                    <TableCell align="right">{r.used != null ? num(r.used) : ''}</TableCell>
                                    <TableCell align="right">{r.bereavement != null ? num(r.bereavement) : ''}</TableCell>
                                    <TableCell>{r.notes || ''}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Granted in this app</Typography>
            {local.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No app grants yet.</Typography>
            ) : (
                <Box sx={{ mt: 1 }}>
                    {local.map((r) => (
                        <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Chip size="small" label={r.type === 'bereavement' ? 'Bereavement' : 'PTO'} color={r.type === 'bereavement' ? 'default' : 'primary'} />
                            <Typography variant="body2">
                                {formatDateUS(r.startDate)}{r.endDate && r.endDate !== r.startDate ? ` – ${formatDateUS(r.endDate)}` : ''}
                            </Typography>
                            <Chip size="small" variant="outlined" label={`${num(r.totalDays)} day(s)`} />
                            <Chip size="small" variant="outlined" label={r.status} />
                            <Box sx={{ flex: 1 }} />
                            {r.createdByName && <Typography variant="caption" color="text.secondary">by {r.createdByName}</Typography>}
                        </Box>
                    ))}
                </Box>
            )}
        </Card>
    );
}
